/**
 * R8 Sources tab — the operational triage view. Per CD spec:
 *   - Proportionate KPI strip: problems dominate, live + idle quiet
 *   - Triage banner at top (default mode): degraded + rotting only
 *   - All-sources mode: full audit table, sorted worst-first
 *   - BLOCKED subsection: promoted, collapsed by default, NEVER hidden
 *   - Per-source 30-day history strip
 *   - Expandable row: workaround panel for blocked, action verbs otherwise
 *   - Region lens (Phase 6 persona-lens reuse)
 */

import { useMemo, useState } from 'react';
import { StateBadge, HistoryStrip, HistoryStripExpanded, ReasonTag, FhBtn } from './primitives';
import { LicenseBadge } from './license-primitives';
import { STATE_META, REASON_META, type FeedState } from './types';
import type { SampleFeedSource } from './sample-data';
import { TOTAL_REAL_SOURCES } from './sample-data';
import {
  getLicense, getSourceClearance, getSourceHistory, getHistoryCoverage,
} from '@biomax/plugin-surveillance-feed';
import { FEED_HISTORY } from '../catalog';

export interface SourcesViewProps {
  sources: readonly SampleFeedSource[];
  region: string;
}

export function SourcesView({ sources: all, region }: SourcesViewProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAllBlocked, setShowAllBlocked] = useState(false);
  const [mode, setMode] = useState<'triage' | 'all'>('triage');

  const sources = useMemo(
    () => all.filter(s => region === 'all' || s.region === region || s.region === 'Global'),
    [all, region],
  );
  const problems = sources.filter(s => s.state === 'degraded' || s.state === 'rotting');
  const blocked = sources.filter(s => s.state === 'blocked');
  const healthy = sources.filter(s => s.state === 'live' || s.state === 'idle' || s.state === 'stale');

  // KPI counts (across all sources in this region — proportionate to operational triage).
  const counts = useMemo(() => {
    const c: Record<FeedState, number> = { live: 0, idle: 0, stale: 0, rotting: 0, degraded: 0, blocked: 0 };
    for (const s of sources) c[s.state]++;
    return c;
  }, [sources]);

  const histCov = getHistoryCoverage(FEED_HISTORY);

  return (
    <div>
      {/* Phase 7.2 honesty rail — declares whether the 30-day strips are
          drawn from real ingest history or synthetic sample data. */}
      <div style={{
        marginBottom: 10, padding: '6px 10px',
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
        color: histCov.days > 0 ? 'var(--bm-state-live)' : 'var(--bm-text-subtle)',
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-sm)',
      }}>
        {histCov.days > 0
          ? <>● 30-day strips: REAL ingest history · {histCov.days}/30 days persisted ({histCov.earliest} → {histCov.latest})</>
          : <>○ 30-day strips: ILLUSTRATIVE (no live history yet — run <code>npm run ingest</code> daily to populate)</>}
      </div>

      {/* Proportionate KPI strip — problems dominate visually */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8,
        marginBottom: 16,
      }}>
        <Kpi big n={counts.degraded + counts.rotting} label="need attention" sub="degraded + rotting"
          colorVar="var(--bm-state-degraded)" borderVar="var(--bm-state-degraded)" />
        <Kpi big n={counts.blocked} label="blocked" sub="structural"
          colorVar="var(--bm-state-blocked)" borderVar="var(--bm-state-blocked)" />
        <Kpi n={counts.stale} label="stale" colorVar="var(--bm-state-stale)" />
        <Kpi n={counts.live} sub={`/ ${TOTAL_REAL_SOURCES}`} label="live" quiet />
      </div>

      {/* TRIAGE banner — always featured at top */}
      <Band>
        <BandHead title={`⚠ Needs attention now · ${problems.length}`}>
          <ModeToggle mode={mode} setMode={setMode} />
        </BandHead>
        {problems.length === 0 ? (
          <div style={{ padding: '14px 12px', fontSize: 13, color: 'var(--bm-state-live)' }}>
            ✓ No transient failures in this region.
            {blocked.length > 0 ? ` ${blocked.length} structurally blocked (below).` : ' All sources healthy.'}
          </div>
        ) : (
          problems.map(s => (
            <Row key={s.id} s={s} expanded={expanded === s.id} onToggle={() => setExpanded(expanded === s.id ? null : s.id)} />
          ))
        )}
      </Band>

      {/* ALL sources (audit) — region-FIRST collapse with badge counts (CD R9
          Q2 verdict: "region-first collapse × persona lens as a filter on
          top — not either/or"). Each region is its own band, collapsed by
          default, badge-counting need-attention + blocked. Operator opens
          one region at a time. Scales to 100+ countries.
          Persona-lens pre-expansion is the Phase 9 add (needs the persona-
          to-region mapping CD will define in R10). */}
      {mode === 'all' && (
        <RegionGroupedSources
          sources={sources}
          expanded={expanded}
          setExpanded={setExpanded}
          regionFilter={region}
        />
      )}

      {/* BLOCKED subsection — promoted, never hidden, collapsed by default */}
      {mode === 'triage' && blocked.length > 0 && (
        <Band borderVar="var(--bm-blocked-ring)">
          <button
            type="button"
            onClick={() => setShowAllBlocked(v => !v)}
            style={{
              ...BAND_HEAD_STYLE,
              cursor: 'pointer', border: 'none', textAlign: 'left' as const, width: '100%',
              background: 'transparent',
            }}
          >
            <span style={{ ...BAND_TITLE_STYLE, color: 'var(--bm-blocked)' }}>
              🔒 Structurally blocked · {blocked.length}
            </span>
            <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)' }}>
              {showAllBlocked ? '▾ hide' : '▸ show'} · excluded from alerts · known gaps
            </span>
          </button>
          {showAllBlocked && blocked.map(s => (
            <Row key={s.id} s={s} expanded={expanded === s.id} onToggle={() => setExpanded(expanded === s.id ? null : s.id)} />
          ))}
        </Band>
      )}

      {mode === 'triage' && (
        <div style={{
          marginTop: 10, padding: '10px 14px',
          fontSize: 12, color: 'var(--bm-text-subtle)',
          background: 'var(--bm-bg-subtle)',
          border: '1px solid var(--bm-border)',
          borderRadius: 'var(--bm-radius-sm)',
        }}>
          <span style={{ color: 'var(--bm-state-live)' }}>✓ {healthy.length} healthy sources</span> not
          shown in triage · switch to <b>All sources</b> to audit the full list.
        </div>
      )}
    </div>
  );
}

/** Phase 8.4-leftover-3: region-first collapse for the All-sources audit
 *  mode. Each region is its own collapsed band with badge counts —
 *  operator opens one at a time. Scales cleanly to 100+ countries because
 *  the operator never sees a flat list of all sources at once.
 *
 *  Persona-lens pre-expansion (CD R9 verdict's "× persona lens as a
 *  filter on top") is Phase 9 — needs persona→region mapping from CD R10. */
function RegionGroupedSources({
  sources, expanded, setExpanded, regionFilter,
}: {
  sources: readonly SampleFeedSource[];
  expanded: string | null;
  setExpanded: (id: string | null) => void;
  regionFilter: string;
}) {
  const grouped = useMemo(() => {
    const byRegion = new Map<string, SampleFeedSource[]>();
    for (const s of sources) {
      const arr = byRegion.get(s.region) ?? [];
      arr.push(s);
      byRegion.set(s.region, arr);
    }
    // Sort each region's sources worst-first
    for (const arr of byRegion.values()) {
      arr.sort((a, b) => STATE_META[b.state].rank - STATE_META[a.state].rank);
    }
    // Sort regions: most-need-attention first, then by source count
    const entries = [...byRegion.entries()].map(([region, items]) => {
      const need = items.filter(s => s.state === 'degraded' || s.state === 'rotting').length;
      const blocked = items.filter(s => s.state === 'blocked').length;
      return { region, items, need, blocked };
    });
    entries.sort((a, b) => (b.need + b.blocked) - (a.need + a.blocked) || b.items.length - a.items.length);
    return entries;
  }, [sources]);

  // Open by default: any region with need-attention or blocked sources.
  // Operator can collapse/expand each independently from there.
  const [openRegions, setOpenRegions] = useState<Set<string>>(() => {
    const out = new Set<string>();
    for (const g of grouped) {
      if (g.need + g.blocked > 0) out.add(g.region);
    }
    return out;
  });
  const toggleRegion = (region: string) => {
    setOpenRegions(prev => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });
  };

  return (
    <Band>
      <BandHead title={`All sources by region · ${sources.length}${regionFilter !== 'all' ? ` (filter: ${regionFilter})` : ''}`}>
        <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)' }}>
          regions sorted by need-attention · click to expand
        </span>
      </BandHead>
      {grouped.map(g => {
        const isOpen = openRegions.has(g.region);
        return (
          <div key={g.region} style={{ borderBottom: '1px solid var(--bm-border)' }}>
            <button
              type="button"
              onClick={() => toggleRegion(g.region)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', textAlign: 'left' as const,
                padding: '8px 12px',
                border: 'none', background: 'transparent', cursor: 'pointer',
                fontFamily: 'var(--bm-font-mono)', fontSize: 12,
              }}
            >
              <span style={{ color: 'var(--bm-text-subtle)', fontSize: 11 }}>{isOpen ? '▾' : '▸'}</span>
              <span style={{ fontWeight: 700, color: 'var(--bm-text)', minWidth: 70 }}>{g.region}</span>
              <span style={{ color: 'var(--bm-text-muted)' }}>{g.items.length} source{g.items.length === 1 ? '' : 's'}</span>
              <span style={{ flex: 1 }} />
              {g.need > 0 && (
                <span style={{
                  padding: '1px 8px', borderRadius: 999,
                  fontSize: 10.5, fontWeight: 700,
                  background: 'var(--bm-state-degraded-bg)',
                  border: '1px solid var(--bm-state-degraded)',
                  color: 'var(--bm-state-degraded)',
                }}>{g.need} need attention</span>
              )}
              {g.blocked > 0 && (
                <span style={{
                  padding: '1px 8px', borderRadius: 999,
                  fontSize: 10.5, fontWeight: 700,
                  background: 'var(--bm-state-blocked-bg)',
                  border: '1px solid var(--bm-blocked-ring)',
                  color: 'var(--bm-blocked)',
                }}>{g.blocked} blocked</span>
              )}
            </button>
            {isOpen && g.items.map(s => (
              <Row key={s.id} s={s} expanded={expanded === s.id} onToggle={() => setExpanded(expanded === s.id ? null : s.id)} />
            ))}
          </div>
        );
      })}
    </Band>
  );
}

const BAND_HEAD_STYLE = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '8px 12px',
  borderBottom: '1px solid var(--bm-border)',
};
const BAND_TITLE_STYLE = {
  fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 700, letterSpacing: '.4px',
  color: 'var(--bm-text)',
};

function Band({ children, borderVar }: { children: React.ReactNode; borderVar?: string }) {
  return (
    <section style={{
      marginBottom: 14,
      background: 'var(--bm-bg-subtle)',
      border: `1px solid ${borderVar ?? 'var(--bm-border)'}`,
      borderRadius: 'var(--bm-radius-md)',
      overflow: 'hidden',
    }}>{children}</section>
  );
}

function BandHead({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <header style={BAND_HEAD_STYLE}>
      <span style={BAND_TITLE_STYLE}>{title}</span>
      {children}
    </header>
  );
}

function ModeToggle({ mode, setMode }: { mode: 'triage' | 'all'; setMode: (m: 'triage' | 'all') => void }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--bm-border)', borderRadius: 5 }}>
      {(['triage', 'all'] as const).map(k => (
        <button
          key={k}
          type="button"
          onClick={() => setMode(k)}
          style={{
            padding: '3px 9px', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
            background: mode === k ? 'var(--bm-bg-strong)' : 'transparent',
            color: mode === k ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
          }}
        >{k === 'triage' ? 'Triage' : 'All sources'}</button>
      ))}
    </div>
  );
}

function Kpi({ n, label, sub, colorVar, borderVar, big = false, quiet = false }: {
  n: number;
  label: string;
  sub?: string;
  colorVar?: string;
  borderVar?: string;
  big?: boolean;
  quiet?: boolean;
}) {
  return (
    <div style={{
      padding: '10px 12px',
      background: quiet ? 'transparent' : 'var(--bm-bg-subtle)',
      border: `1px solid ${borderVar ?? 'var(--bm-border)'}`,
      borderRadius: 'var(--bm-radius-sm)',
      opacity: quiet ? 0.8 : 1,
    }}>
      <div style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: big ? 22 : 17, fontWeight: 700,
        color: colorVar ?? 'var(--bm-text)',
      }}>{n.toLocaleString()}{sub ? <span style={{ color: 'var(--bm-text-subtle)', fontWeight: 400, fontSize: 12 }}> {sub}</span> : null}</div>
      <div style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
        letterSpacing: '.4px', textTransform: 'uppercase',
        color: 'var(--bm-text-subtle)', marginTop: 2,
      }}>{label}</div>
    </div>
  );
}

function Row({ s, expanded, onToggle }: { s: SampleFeedSource; expanded: boolean; onToggle: () => void }) {
  const m = STATE_META[s.state];
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.5fr) 88px 90px 140px 70px 78px 16px',
          gap: 10, alignItems: 'center',
          padding: '8px 12px',
          width: '100%', border: 'none', textAlign: 'left' as const,
          background: expanded ? 'var(--bm-bg)' : 'transparent',
          borderLeft: `3px solid ${m.cssVar}`,
          borderBottom: '1px solid var(--bm-border)',
          fontSize: 12, lineHeight: 1.3, cursor: 'pointer',
        }}
      >
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontWeight: 600, color: 'var(--bm-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: 'var(--bm-font-mono)', fontSize: 10, color: 'var(--bm-text-subtle)',
          }}>
            {s.region}
            <LicenseBadge sourceId={s.id} small />
          </span>
        </span>
        <StateBadge state={s.state} small />
        {s.reason ? <ReasonTag reason={s.reason} /> : <span />}
        <HistoryStrip history={resolveHistory(s)} />
        <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, textAlign: 'right' as const, color: 'var(--bm-text-muted)' }}>
          {s.signals > 0 ? s.signals.toLocaleString() : '—'}<span style={{ color: 'var(--bm-text-subtle)' }}> sig</span>
        </span>
        <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)' }}>
          {s.latestDays === 0 ? 'today' : `${s.latestDays}d`}
          <span style={{ color: 'var(--bm-text-subtle)' }}> · {s.cadence}</span>
        </span>
        <span style={{ color: 'var(--bm-text-subtle)' }}>{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div style={{
          padding: '10px 14px',
          background: 'var(--bm-bg)',
          borderBottom: '1px solid var(--bm-border)',
        }}>
          {s.note && (
            <div style={{
              fontSize: 12, color: 'var(--bm-text-muted)', marginBottom: 8,
            }}>
              {m.transient === false ? '' : '⚠ '}{s.note}
              {s.since && <span style={{ color: 'var(--bm-text-subtle)' }}> · since {s.since}</span>}
            </div>
          )}
          {/* Phase 8.4-leftover-2: expand-on-click larger strip with per-day
              tooltips + axis labels. Sample fallback strips also render here
              when no live history is available — the resolveHistory helper
              picks the right source per row. */}
          <div style={{
            marginBottom: 10, padding: '8px 10px',
            background: 'var(--bm-bg-subtle)',
            border: '1px solid var(--bm-border)',
            borderRadius: 5,
          }}>
            <div style={{
              fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 600,
              letterSpacing: '.5px', textTransform: 'uppercase',
              color: 'var(--bm-text-subtle)', marginBottom: 6,
            }}>30-day status · hover any day</div>
            <HistoryStripExpanded history={resolveHistory(s)} width={460} height={42} />
          </div>
          {s.state === 'blocked' && s.reason && (
            <div style={{
              padding: 10,
              background: 'var(--bm-state-blocked-bg)',
              border: '1px solid var(--bm-blocked-ring)',
              borderRadius: 5,
            }}>
              <div style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 700,
                color: 'var(--bm-blocked)', marginBottom: 6,
              }}>🔒 Structurally blocked · {REASON_META[s.reason].word}</div>
              <div style={{ fontSize: 12, color: 'var(--bm-text)', marginBottom: 10, lineHeight: 1.5 }}>
                {REASON_META[s.reason].detail}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <FhBtn>Request workaround</FhBtn>
                <FhBtn ghost>Exclude from alerts</FhBtn>
                <FhBtn ghost>Docs ↗</FhBtn>
              </div>
            </div>
          )}
          {s.state !== 'blocked' && (
            <div style={{ display: 'flex', gap: 6 }}>
              <FhBtn>Retry now</FhBtn>
              <FhBtn ghost>Snooze 24h</FhBtn>
              <FhBtn ghost>Export for brief</FhBtn>
            </div>
          )}
          <LicensePanel sourceId={s.id} />
        </div>
      )}
    </>
  );
}

/** Pick real history when the register has any coverage for this source;
 *  fall back to the sample's synthetic strip so the surface always renders
 *  exactly 30 cells. */
function resolveHistory(s: SampleFeedSource): readonly FeedState[] {
  if (FEED_HISTORY.days.length === 0) return s.history;
  const real = getSourceHistory(FEED_HISTORY, s.id, 30);
  // If the source has zero real entries in the register, the strip would
  // be all-idle which is misleading — fall back to the sample state.
  const realDayCount = FEED_HISTORY.days.filter(d => d.states[s.id] != null).length;
  return realDayCount > 0 ? real : s.history;
}

function LicensePanel({ sourceId }: { sourceId: string }) {
  const entry = getLicense(sourceId);
  const clearance = getSourceClearance(sourceId);
  if (!entry) {
    return (
      <div style={{
        marginTop: 10, padding: 10,
        background: 'var(--bm-state-rotting-bg)',
        border: '1px solid var(--bm-state-rotting)',
        borderRadius: 5,
        fontSize: 11.5, color: 'var(--bm-state-rotting)',
      }}>
        ⚠ No entry in source-licenses.json — registry gap (CI bug).
      </div>
    );
  }
  const restrictive = clearance !== 'cleared';
  return (
    <div style={{
      marginTop: 10, padding: 10,
      background: 'var(--bm-bg-subtle)',
      border: `1px solid ${restrictive ? 'var(--bm-state-degraded)' : 'var(--bm-border)'}`,
      borderRadius: 5,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        marginBottom: 6, flexWrap: 'wrap',
      }}>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
          letterSpacing: '.4px', textTransform: 'uppercase',
          color: 'var(--bm-text-muted)',
        }}>License</span>
        <LicenseBadge sourceId={sourceId} />
        <span style={{ fontSize: 11.5, color: 'var(--bm-text)' }}>{entry.license}</span>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--bm-text-muted)', marginBottom: 4 }}>
        <b>Publisher:</b> {entry.publisher}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--bm-text-muted)', marginBottom: 6 }}>
        <b>Attribution:</b> <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11 }}>
          {entry.attributionText}
        </span>
      </div>
      {entry.operatorWarnings.length > 0 && (
        <ul style={{
          margin: '6px 0 0', padding: '0 0 0 18px',
          fontSize: 11.5, color: 'var(--bm-text-muted)', lineHeight: 1.45,
        }}>
          {entry.operatorWarnings.map((w, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{w}</li>
          ))}
        </ul>
      )}
      <div style={{ marginTop: 6 }}>
        <a
          href={entry.verifyUrl}
          target="_blank" rel="noreferrer"
          style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
            color: 'var(--bm-accent, #6ea3ff)', textDecoration: 'none',
          }}
        >Terms page ↗</a>
        {entry.licenseVerified && entry.verifiedAt && (
          <span style={{ marginLeft: 10, fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>
            Verified {entry.verifiedAt} · {entry.verifiedBy ?? 'unknown'}
          </span>
        )}
      </div>
    </div>
  );
}
